import {Injectable, inject} from '@angular/core';
import {Router} from '@angular/router';
import {Observable, of} from 'rxjs';
import {catchError, finalize, map, shareReplay, take} from 'rxjs/operators';
import {LoginApiService} from '@shared/services/api/login-page/login-api.service';
import {AuthService} from './auth.service';

/**
 * Kết thúc session Page nhưng vẫn giữ session tài khoản.
 *
 * Tương đương `PageService.outPages()` của FE1: dùng cho lỗi token Page/Zalo
 * khi gửi tin, không dùng `AuthService.logout()` để tránh đăng xuất toàn bộ user.
 */
@Injectable({providedIn: 'root'})
export class PageSessionService {
  private readonly loginApi = inject(LoginApiService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private isEnding = false;
  private hasVerifiedActivePageSession = false;
  private verifyActivePageSession$: Observable<boolean> | null = null;

  /**
   * `usr` chỉ là cache FE; session Page ở BE có thể mất sau khi refresh server
   * hoặc sau khi mở lại tab. Khôi phục nó bằng đúng API joinpages trước khi cho
   * vào khu vực hội thoại để BE không nhận `active page = null`.
   */
  public ensureActivePageSession(): Observable<boolean> {
    if (this.hasVerifiedActivePageSession) {
      return of(true);
    }

    if (this.verifyActivePageSession$) {
      return this.verifyActivePageSession$;
    }

    const pageIds = this.authService.getPageIds()
      .map(pageId => String(pageId))
      .filter(Boolean);
    if (!pageIds.length) {
      return of(false);
    }

    this.verifyActivePageSession$ = this.loginApi.userJoinPages({ids: pageIds, affiliate: ''}).pipe(
      take(1),
      map((response: any) => {
        const data = response?.data && typeof response.data === 'object'
          ? response.data
          : response;
        const code = Number(response?.code ?? data?.code);
        const restoredPageIds = Array.isArray(data?.pageIds)
          ? data.pageIds.map((pageId: string | number) => String(pageId)).filter(Boolean)
          : Array.isArray(data?.pages)
            ? data.pages.map((page: {id?: string | number}) => String(page?.id ?? '')).filter(Boolean)
            : [];

        if (code !== 1 || !restoredPageIds.length) {
          this.authService.outPages();
          return false;
        }

        this.authService.updateAuthorization(data);
        this.authService.isSelectPage = true;
        this.hasVerifiedActivePageSession = true;
        return true;
      }),
      catchError(() => {
        // Không giữ cache Page cũ khi BE không xác nhận được session hiện tại.
        this.authService.outPages();
        return of(false);
      }),
      shareReplay({bufferSize: 1, refCount: false})
    );

    return this.verifyActivePageSession$;
  }

  /** Join page vừa hoàn tất trong phiên hiện tại, không cần gọi lại API. */
  public markActivePageSessionVerified(): void {
    this.hasVerifiedActivePageSession = true;
  }

  public endPageSession(): void {
    if (this.isEnding) return;
    this.isEnding = true;

    this.loginApi.logOutPage()
      .pipe(
        take(1),
        // Dù BE phản hồi lỗi vẫn phải xoá state Page local để không kẹt màn hình chat.
        finalize(() => this.finish())
      )
      .subscribe({error: () => undefined});
  }

  private finish(): void {
    this.hasVerifiedActivePageSession = false;
    this.verifyActivePageSession$ = null;
    this.authService.outPages();
    void this.router.navigate(['/auth', 'login']);
  }
}
